import { Input, Typography } from "antd";
import { useSelectedNode } from "components/CC/hooks/useDiagramActions";
import { FormValueSync } from "components/CC/UI/FormValueSync";
import { GeneralFormProvider } from "components/UI/GeneralForm/GeneralFormProvider.tsx";
import { useGeneralForm } from "components/UI/GeneralForm/hooks/useGeneralForm.ts";
import { ScenarioSelectSearchable } from "components/UI/Selects/ScenarioSelectSearchable/ScenarioSelectSearchable";
import { usePaldockTranslation } from "hooks/usePaldockTranslation.tsx";
import { Controller } from "react-hook-form";
import { FormItem } from "react-hook-form-antd";
import { z } from "zod";

/**
 * SubScenario Form Data
 *
 * BE expects: { scenario_id: string, start_step_id?: string | null }
 * FE uses: { scenario: string } which maps to BE scenario_id
 *
 * See settingsMappers.ts for the mapping.
 */
const subScenarioFormSchema = z.object({
    active: z.boolean(),
    title: z.string(),
    subtitle: z.string(),
    scenario: z.string(), // Maps to BE scenario_id
});

export type SubScenarioFormData = z.infer<typeof subScenarioFormSchema>;

interface FormProps {
    onChange?: (data: SubScenarioFormData) => void;
    formId: string;
}

export const SubScenarioForm = ({ onChange, formId }: FormProps) => {
    const { selectedNode } = useSelectedNode();
    return (
        <GeneralFormProvider<SubScenarioFormData>
            key={selectedNode?.id}
            id={formId}
            schema={subScenarioFormSchema}
            defaultValues={selectedNode?.formData as SubScenarioFormData}
        >
            <FormContent onChange={onChange} />
        </GeneralFormProvider>
    );
};

const FormContent = ({ onChange }: { onChange?: (data: SubScenarioFormData) => void }) => {
    const { control } = useGeneralForm<SubScenarioFormData>();
    const { t } = usePaldockTranslation("components.connection-creator");

    return (
        <div className="bg-white rounded-lg p-4 m-3">
            <FormValueSync<SubScenarioFormData> onChange={onChange} />
            {/* Header with title and active toggle */}
            <div className="flex justify-between items-center mb-4">
                <Typography.Title level={4} className="!mb-0">
                    {t("nodes.sub_scenario.forms.settings")}
                </Typography.Title>
                {/* Active toggle — hidden until BE is_enabled is wired up
                <Controller
                    control={control}
                    name="active"
                    render={({ field }) => (
                        <div className="flex items-center gap-2">
                            <Switch checked={field.value} onChange={field.onChange} />
                            <Typography.Text type="secondary">{t("nodes.sub_scenario.forms.active")}</Typography.Text>
                        </div>
                    )}
                /> */}
            </div>

            {/* Title and Subtitle row */}
            <div className="flex gap-4 mb-4">
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="title"
                    label={t("nodes.sub_scenario.forms.title")}
                >
                    <Input placeholder={t("nodes.sub_scenario.forms.title_placeholder")} />
                </FormItem>
                <FormItem
                    className="flex-1 !mb-0"
                    control={control}
                    name="subtitle"
                    label={t("nodes.sub_scenario.forms.subtitle")}
                >
                    <Input placeholder={t("nodes.sub_scenario.forms.subtitle_placeholder")} />
                </FormItem>
            </div>

            {/* Scenario dropdown - loads scenarios from API */}
            <Controller
                control={control}
                name="scenario"
                render={({ field }) => (
                    <div>
                        <Typography.Text className="block mb-2">
                            {t("nodes.sub_scenario.forms.scenario")}
                        </Typography.Text>
                        <ScenarioSelectSearchable
                            value={field.value || undefined}
                            onChange={(value) => field.onChange(value ?? "")}
                            allowClear
                            className="w-full"
                        />
                    </div>
                )}
            />
        </div>
    );
};
